import type { Database } from "@/types/database";

/**
 * list_control_board bleibt jsonb. src/types/database.ts führt die Funktion
 * deshalb weiter als Returns: Json (Signatur unverändert, Diff-Guard der
 * Typengenerierung). Die zusätzlichen Felder stehen auf BoardCase.
 * Fehlen sie (Migration noch nicht angewendet), bleiben sie null und die
 * Tabelle zeigt „–“.
 */
export type ListControlBoardFn = Database["public"]["Functions"]["list_control_board"];

export type BoardTaskStatus = "overdue" | "due";

export type BoardTask = {
  kind: "daily_check" | "weight" | "medication_dose" | string;
  /**
   * Fälligkeit der Aufgabe. `list_control_board` und `list_case_open_tasks`
   * setzen `due_at`. Die Medikamentenvorschau trägt die Zeit in `planned_at`;
   * fehlt `due_at` im Payload oder ist es leer, übernimmt der Parser `planned_at`.
   * Ohne beide bleibt das Feld leer. fehlt gilt dann nicht. Mit status folgt
   * die Zeile open_tasks[].status. Ohne status ist eine Nicht-Medikament-Aufgabe
   * mit due_at in der Vergangenheit überfällig, sonst „n offen“.
   */
  due_at: string;
  /** Rohfeld der Medikamentenvorschau, falls der Payload es mitliefert. */
  planned_at?: string | null;
  interval_hours: number | null;
  occurrence_key: string | null;
  label: string;
  /** Gesetzt von list_control_board. list_case_open_tasks liefert das Feld nicht. */
  status?: BoardTaskStatus | null;
  plan_id?: string;
  plan_revision_id?: string;
  therapy_kind?: string;
};

export type BoardCase = {
  case_id: string;
  organization_id: string;
  case_number: string | null;
  name: string | null;
  status: string;
  sex: string;
  age_class: string;
  current_care_site_id: string | null;
  /** Leer, wenn der Endpunkt keinen Namen liefert. Dann clientseitig auflösen. */
  current_care_site_name: string | null;
  current_place_id: string | null;
  open_task_count: number;
  open_tasks: BoardTask[];
  last_weight_grams: number | null;
  last_weight_at: string | null;
  previous_weight_grams: number | null;
  next_due_at: string | null;
  /**
   * Art am Fall. list_control_board setzt species_name und species_unassigned (#57).
   * Fehlen die Schlüssel, bleibt der Parser tolerant.
   */
  species_name: string | null;
  species_unassigned: boolean;
};

export type ControlBoardFeedItem = {
  kind: string;
  case_id: string;
  case_number?: string | null;
  name?: string | null;
  occurred_at: string;
  label: string;
  species_name?: string | null;
  species_unassigned?: boolean;
};

export type ControlBoard = {
  mode: "care_board" | "member_feed";
  board_density: "compact" | "expanded";
  cases: BoardCase[];
  feed: ControlBoardFeedItem[];
  has_care_access: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function optionalText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function readBoardTaskStatus(value: unknown): BoardTaskStatus | null {
  return value === "overdue" || value === "due" ? value : null;
}

export function readBoardTask(value: unknown): BoardTask | null {
  const row = asRecord(value);
  if (!row || typeof row.kind !== "string" || typeof row.label !== "string") return null;
  const plannedAt = optionalText(row.planned_at);
  const dueAt = optionalText(row.due_at) ?? plannedAt ?? "";
  const task: BoardTask = {
    kind: row.kind,
    due_at: dueAt,
    interval_hours: asNumber(row.interval_hours),
    occurrence_key: asString(row.occurrence_key),
    label: row.label,
    status: readBoardTaskStatus(row.status),
  };
  if (plannedAt) task.planned_at = plannedAt;
  if (typeof row.plan_id === "string") task.plan_id = row.plan_id;
  if (typeof row.plan_revision_id === "string") task.plan_revision_id = row.plan_revision_id;
  if (typeof row.therapy_kind === "string") task.therapy_kind = row.therapy_kind;
  return task;
}

export function readBoardCase(value: unknown): BoardCase | null {
  const row = asRecord(value);
  if (!row || typeof row.case_id !== "string") return null;
  const tasks = Array.isArray(row.open_tasks)
    ? row.open_tasks.flatMap((item) => {
        const task = readBoardTask(item);
        return task ? [task] : [];
      })
    : [];
  return {
    case_id: row.case_id,
    organization_id: asString(row.organization_id) ?? "",
    case_number: asString(row.case_number),
    name: asString(row.name),
    status: asString(row.status) ?? "",
    sex: asString(row.sex) ?? "",
    age_class: asString(row.age_class) ?? "",
    current_care_site_id: asString(row.current_care_site_id),
    current_care_site_name: optionalText(row.current_care_site_name),
    current_place_id: asString(row.current_place_id),
    open_task_count: asNumber(row.open_task_count) ?? tasks.length,
    open_tasks: tasks,
    last_weight_grams: asNumber(row.last_weight_grams),
    last_weight_at: asString(row.last_weight_at),
    previous_weight_grams: asNumber(row.previous_weight_grams),
    next_due_at: asString(row.next_due_at),
    species_name: optionalText(row.species_name),
    species_unassigned: row.species_unassigned === true,
  };
}

export function readControlBoard(data: unknown): ControlBoard {
  const row = asRecord(data);
  const cases = Array.isArray(row?.cases)
    ? row.cases.flatMap((item) => {
        const boardCase = readBoardCase(item);
        return boardCase ? [boardCase] : [];
      })
    : [];
  const feed = Array.isArray(row?.feed)
    ? row.feed.flatMap((item) => {
        const entry = asRecord(item);
        if (!entry || typeof entry.kind !== "string" || typeof entry.case_id !== "string") {
          return [];
        }
        const occurredAt = asString(entry.occurred_at);
        const label = asString(entry.label);
        if (!occurredAt || !label) return [];
        return [
          {
            kind: entry.kind,
            case_id: entry.case_id,
            case_number: asString(entry.case_number),
            name: asString(entry.name),
            occurred_at: occurredAt,
            label,
            species_name: asString(entry.species_name),
            species_unassigned:
              typeof entry.species_unassigned === "boolean" ? entry.species_unassigned : undefined,
          },
        ];
      })
    : [];
  return {
    mode: row?.mode === "care_board" ? "care_board" : "member_feed",
    board_density: row?.board_density === "expanded" ? "expanded" : "compact",
    cases,
    feed,
    has_care_access: row?.has_care_access === true,
  };
}
